import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Droplets, HelpCircle, FileCheck, ChevronDown } from 'lucide-react';

const facts = [
  {
    title: 'Technische Daten',
    gradient: 'from-[hsl(198,50%,36%)] to-[hsl(198,55%,28%)]',
    stats: [
      { value: '1–200', label: 'Stückzahlen' },
      { value: 'H7', label: 'Toleranzqualität' },
    ],
    description: 'CNC Fräsen von Einzelteilen und Kleinserien mit höchster Präzision.',
  },
  {
    title: 'Aluminium',
    gradient: 'from-[hsl(198,60%,42%)] to-[hsl(198,70%,35%)]',
    stats: [
      { value: '24+', label: 'Legierungen' },
      { value: '3.1354', label: 'Luftfahrt-Alu' },
    ],
    description: 'Fokus auf Aluminiumbearbeitung.',
    items: [
      'AlMg4,5Mn0,7 (EN AW 5083)',
      'AlSi1MgMn (EN AW 6082)',
      'AlZnMgCu1,5 (EN AW 7075)',
      'Luftfahrt-Aluminium 3.1354 u.\u00a0a.',
    ],
  },
  {
    title: 'Stähle',
    gradient: 'from-[hsl(200,15%,30%)] to-[hsl(200,10%,22%)]',
    stats: [
      { value: '18+', label: 'Stahlsorten' },
    ],
    description: 'Baustähle, Einsatzstähle und Werkzeugstähle.',
    items: ['S355', 'C45', '16MnCr5', '90MnCrV8', 'u.\u00a0v.\u00a0m.'],
  },
  {
    title: 'Kunststoffe',
    gradient: 'from-[hsl(198,45%,40%)] to-[hsl(198,50%,30%)]',
    stats: [
      { value: '17+', label: 'Kunststoffarten' },
    ],
    description: 'Technische Kunststoffe für anspruchsvolle Anwendungen.',
    items: ['POM', 'PE1000', 'PA6', 'PEEK', 'u.\u00a0v.\u00a0m.'],
  },
];

const extras = [
  {
    icon: Droplets,
    title: 'Wasserstrahl & Laser',
    text: 'Sämtliche Materialien auch als Wasserstrahl- oder Laserschnitt durch unsere langjährigen Partner.',
  },
  {
    icon: HelpCircle,
    title: 'Sondermaterialien',
    text: 'Andere Materialien jederzeit auf Anfrage möglich.',
  },
  {
    icon: FileCheck,
    title: 'Prüfzeugnis 3.1',
    text: 'Bei Bedarf alle Werkstoffe mit Abnahmeprüfzeugnis 3.1.',
  },
];

const FactCard = ({ fact, index }: { fact: typeof facts[0]; index: number }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.12 }}
      className="relative group"
    >
      {/* Card with gradient header */}
      <div className="rounded-2xl overflow-hidden border border-border bg-card shadow-md hover:shadow-xl transition-shadow duration-500">
        {/* Gradient header area */}
        <div className={`bg-gradient-to-br ${fact.gradient} p-6 pb-8 relative overflow-hidden`}>
          {/* Decorative circles */}
          <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10 blur-sm" />
          <div className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full bg-white/5" />

          <h3 className="font-heading font-bold text-xl text-white relative z-10">{fact.title}</h3>
        </div>

        {/* Stats bar */}
        <div className="flex divide-x divide-border">
          {fact.stats.map((stat) => (
            <div key={stat.label} className="flex-1 p-4 text-center">
              <p className="font-heading font-bold text-xl text-primary">{stat.value}</p>
              <p className="text-muted-foreground text-xs mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="p-5 pt-3 border-t border-border">
          <p className="text-muted-foreground text-sm leading-relaxed">{fact.description}</p>

          {fact.items && (
            <>
              <button
                className="flex items-center gap-1.5 mt-4 text-sm font-semibold text-primary hover:opacity-80 transition-opacity"
                onClick={() => setExpanded(!expanded)}
              >
                Materialien
                <motion.span animate={{ rotate: expanded ? 180 : 0 }} transition={{ duration: 0.25 }}>
                  <ChevronDown className="w-4 h-4" />
                </motion.span>
              </button>

              <AnimatePresence>
                {expanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="flex flex-wrap gap-2 mt-3">
                      {fact.items.map((item, i) => (
                        <motion.span
                          key={item}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: i * 0.05 }}
                          className="inline-block text-xs bg-primary/8 text-primary border border-primary/15 rounded-full px-3 py-1.5 font-medium"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const FactsSection = () => {
  return (
    <section id="fakten" className="py-20 lg:py-32 relative overflow-hidden">
      {/* Background accents */}
      <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-primary/3 blur-3xl" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block text-primary font-semibold uppercase tracking-widest text-sm mb-4"
          >
            Auf einen Blick
          </motion.span>
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
            Zahlen &amp; Fakten
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Materialien, Toleranzen und Kapazitäten – transparent und übersichtlich
          </p>
        </motion.div>

        {/* Cards Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {facts.map((fact, i) => (
            <FactCard key={fact.title} fact={fact} index={i} />
          ))}
        </div>

        {/* Extras row */}
        <div className="grid sm:grid-cols-3 gap-4">
          {extras.map((extra, i) => {
            const Icon = extra.icon;
            return (
              <motion.div
                key={extra.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.4 + i * 0.1 }}
                whileHover={{ scale: 1.02 }}
                className="flex items-start gap-4 bg-gradient-to-br from-card to-muted/40 rounded-xl p-5 border border-border hover:border-primary/30 transition-colors duration-300"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-heading font-bold text-sm text-foreground mb-1">{extra.title}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{extra.text}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FactsSection;
